import React from 'react';
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ShieldCheck, User } from 'lucide-react';
import RatingBadge from './RatingBadge';
import StarRating from './StarRating';

function QuestionDisplay({
  question,
  questionIndex,
  employeeResponse = null,
  managerResponse = null,
  adminResponse = null,
  adminComment = '',
  onAdminCommentChange = null,
  viewMode = 'employee'
}) {
  const isEmployeeView = viewMode === 'employee';
  
  return (
    <div className="py-4 sm:py-6 last:pb-6">
      {/* Question Header */}
      <div className="flex items-start gap-3 sm:gap-4 mb-2">
        <div className="h-6 w-6 sm:h-7 sm:w-7 rounded-full bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
          <span className="text-[10px] sm:text-xs font-semibold text-primary">{questionIndex + 1}</span>
        </div>
        <div className="flex-1">
          <p className="text-sm sm:text-[15px] font-medium text-foreground leading-relaxed">{question.text}</p>
        </div>
      </div>

      {/* Employee's Response */}
      {employeeResponse && (
        <div className="ml-0 sm:ml-11 mb-2">
          <Label className="text-[10px] sm:text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 sm:mb-3 flex items-center gap-1.5">
            <User className="h-3 w-3 sm:h-3.5 sm:w-3.5" /> 
            {isEmployeeView ? 'Your Response' : "Employee's Response"}
          </Label>
          <div className="space-y-2">
            <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
              <RatingBadge 
                label={employeeResponse.rating_label} 
                color="primary" 
                size="sm" 
              />
              {employeeResponse.rating > 0 && (
                <StarRating rating={employeeResponse.rating} size={14} />
              )}
            </div>
            {employeeResponse.comment && (
              <p className="text-xs sm:text-sm text-muted-foreground italic">"{employeeResponse.comment}"</p>
            )}
          </div>
        </div>
      )}

      {/* Manager's Response */}
      {managerResponse && (
        <div className="ml-0 sm:ml-11">
          <Label className="text-[10px] sm:text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 sm:mb-3 flex items-center gap-1.5">
            <User className="h-3 w-3 sm:h-3.5 sm:w-3.5" /> 
            {isEmployeeView ? "Manager's Response" : "Manager's Response"}
          </Label>
          <div className="space-y-2">
            <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
              <RatingBadge 
                label={managerResponse.rating_label} 
                color="accent" 
                size="sm" 
              />
              {managerResponse.rating > 0 && (
                <StarRating rating={managerResponse.rating} size={14} />
              )}
            </div>
            {managerResponse.comment && (
              <p className="text-xs sm:text-sm text-muted-foreground italic">"{managerResponse.comment}"</p>
            )}
          </div>
        </div>
      )}

      {/* Empty state */}
      {!employeeResponse && !managerResponse && (
        <div className="ml-0 sm:ml-11">
          <p className="text-xs sm:text-sm text-muted-foreground italic">No responses yet...</p>
        </div>
      )}

      {/* Admin's Comment */}
      {onAdminCommentChange ? (
        <div className="ml-0 sm:ml-11 mt-3 sm:mt-4">
          <Label className="text-[10px] sm:text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 sm:mb-3 flex items-center gap-1.5">
            <ShieldCheck className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
            Admin's Comment (optional)
          </Label>
          <Textarea
            placeholder="Add your comment on this question..."
            value={adminComment}
            onChange={(e) => onAdminCommentChange(e.target.value)}
            className="min-h-[80px] resize-none text-sm"
          />
        </div>
      ) : adminResponse?.comment && (
        <div className="ml-0 sm:ml-11 mt-3 sm:mt-4">
          <Label className="text-[10px] sm:text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 sm:mb-3 flex items-center gap-1.5">
            <ShieldCheck className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
            Admin's Comment
          </Label>
          <p className="text-xs sm:text-sm text-muted-foreground italic">"{adminResponse.comment}"</p>
        </div>
      )}
    </div>
  );
}

export default QuestionDisplay;
